import React, { useMemo } from "react";
import NutritionStats from "./component/NutritionStats";
import AssignMealCard from "./component/AssignMealCard";
import WaterIntakeCard from "./component/WaterIntakeCard";
import SleepCard from "./component/SleepCard";
import GutHealthCard from "./component/GutHealthCard";
import ExerciseCard from "./component/ExerciseCard";
import SupplementsCard from "./component/SupplementsCard";
import MedicineCard from "./component/MedicineCard";
import VitalsCard from "./component/VitalsCard";
import { useAllLogsData } from "../../hooks/useAllLogsData";
import { AllLogsLoadingSkeleton } from "./component/LoadingShimmers";

const styles = `
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .allLogs-fadeIn {
    animation: fadeInUp 0.5s ease-out forwards;
  }
`;

interface AllLogsTabProps {
  userId: number;
  selectedDate?: Date;
}

// Helper function to format date in local timezone (avoid UTC conversion issues)
const formatDateToString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const AllLogsTab: React.FC<AllLogsTabProps> = ({ userId, selectedDate }) => {
  
  const formattedDate = useMemo(() => {
    if (!selectedDate) {
      const today = formatDateToString(new Date());
      console.log("AllLogsTab: Using today's date:", today);
      return today;
    }

    const formatted = formatDateToString(selectedDate);
    console.log("AllLogsTab: Formatted date:", formatted, "from Date object:", selectedDate);
    return formatted;
  }, [selectedDate]);

  console.log("AllLogsTab: Rendering with userId:", userId, "formattedDate:", formattedDate);

  const { data: allLogsData, loading, error, refetch } = useAllLogsData(userId, formattedDate);

  if (loading) {
    return (
      <>
        <style>{styles}</style>
        <AllLogsLoadingSkeleton />
      </>
    );
  }

  if (error) {
    return (
      <>
        <style>{styles}</style>
        <div className="mx-6 my-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-red-800 mb-1">Failed to Load Data</h3>
                <p className="text-sm text-red-600 mb-3">{error}</p>
                <div className="flex items-center gap-3 text-xs text-gray-600">
                  <span>User ID: {userId}</span>
                  <span>•</span>
                  <span>Date: {formattedDate}</span>
                </div>
                <button
                  onClick={() => refetch()}
                  className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md text-sm hover:bg-red-700 transition-colors"
                >
                  Retry
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (!allLogsData) {
    return (
      <>
        <style>{styles}</style>
        <div className="mx-6 my-6">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
            <p className="text-sm text-yellow-800 mb-2">No data available</p>
            <button
              onClick={() => refetch()}
              className="mt-2 px-4 py-2 bg-yellow-600 text-white rounded-md text-sm hover:bg-yellow-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{styles}</style>

      <div className="allLogs-fadeIn">
        <NutritionStats
          nutritionData={allLogsData?.nutrition_stats?.nutritionist_nutrients}
          loggedData={allLogsData?.nutrition_stats?.logged_data}
          loading={false}
        />

        <div className="px-6 py-6 space-y-6">
          {/* Row 1: Meal Card + Water */}
          <div className="grid grid-cols-3 gap-6">
            <AssignMealCard
              mealData={allLogsData?.meal_data}
              mealPlanInfo={allLogsData?.meal_plan_info}
              selectedDate={selectedDate}
            />
            <WaterIntakeCard
              waterCycle={allLogsData?.water_cycle || []}
            />
          </div>

          {/* Row 2: Sleep, Gut, Exercise */}
          <div className="grid grid-cols-3 gap-6">
            <SleepCard
              sleepCycle={allLogsData?.sleep_cycle_details || []}
              selectedDate={formattedDate}
            />
            <GutHealthCard
              poopCycle={allLogsData?.poop_cycle_details || []}
              selectedDate={formattedDate}
            />
            <ExerciseCard
              exercises={allLogsData?.exercises || []}
              selectedDate={formattedDate}
            />
          </div>

          {/* Row 3: Supplements, Medicine, Vitals */}
          <div className="grid grid-cols-3 gap-6">
            <SupplementsCard
              supplements={allLogsData?.supplement_details || []}
              selectedDate={formattedDate}
            />
            <MedicineCard
              medicines={allLogsData?.medicine_details || []}
              selectedDate={formattedDate}
            />
            <VitalsCard
              vitals={allLogsData?.vital_log || []}
              selectedDate={formattedDate}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default AllLogsTab;